import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ListSubheader, MenuItem } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import styled from 'styled-components';
import { COLORS } from '../../Styles/Colors';

export const SEARCHABLE_SELECT_WIDTH = 200;
export const SEARCHABLE_MENU_WIDTH = 340;

export const SearchHeader = styled(ListSubheader)`
  && {
    position: sticky;
    top: 0;
    z-index: 2;
    line-height: normal;
    padding: 10px 12px 8px;
    background: ${COLORS.white};
    border-bottom: 1px solid ${COLORS.lightGray}30;
  }
`;

export const SearchFieldWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border: 1px solid ${COLORS.gray};
  border-radius: 6px;
  background: ${COLORS.white};
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus-within {
    border-color: ${COLORS.teal};
    box-shadow: 0 0 4px ${COLORS.teal}50;
  }
`;

export const DropdownSearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-size: 0.9em;
  font-family: Arial, sans-serif;
  color: ${COLORS.darkGray};
  background: transparent;
  min-width: 0;

  &::placeholder {
    color: ${COLORS.lightGray};
  }
`;

export const ResultHint = styled.div`
  margin-top: 6px;
  font-size: 0.75em;
  color: ${COLORS.lightGray};
  text-align: right;
`;

export const EmptyStateItem = styled(MenuItem)`
  && {
    font-size: 0.9em;
    color: ${COLORS.lightGray};
    font-style: italic;
    justify-content: center;
  }
`;

export const selectFocusSx = {
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: COLORS.gray,
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: COLORS.teal,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: COLORS.teal,
    boxShadow: `0 0 0 3px ${COLORS.teal}25`,
  },
};

export const defaultOptionSx = {
  fontSize: '0.9em',
  '&.Mui-focusVisible': {
    backgroundColor: `${COLORS.teal}15`,
  },
};

export const selectedOptionSx = {
  fontSize: '0.9em',
  '&.Mui-selected': {
    backgroundColor: `${COLORS.teal}18`,
    fontWeight: 600,
    '&:hover': {
      backgroundColor: `${COLORS.teal}25`,
    },
  },
  '&.Mui-focusVisible': {
    backgroundColor: `${COLORS.teal}15`,
  },
  '&:hover': {
    backgroundColor: `${COLORS.teal}10`,
  },
};

interface UseSearchableSelectOptions {
  searchable?: boolean;
  onEnterSelect?: () => void;
}

export function useSearchableSelect({
  searchable = true,
  onEnterSelect,
}: UseSearchableSelectOptions = {}) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [menuWidth, setMenuWidth] = useState(SEARCHABLE_MENU_WIDTH);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const menuListRef = useRef<HTMLUListElement>(null);
  const selectRef = useRef<HTMLDivElement>(null);

  const focusSearchInput = useCallback(() => {
    if (!searchable) return;
    requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
  }, [searchable]);

  const syncMenuWidth = useCallback(() => {
    const viewportCap = typeof window !== 'undefined' ? window.innerWidth - 24 : SEARCHABLE_MENU_WIDTH;
    const anchorWidth = selectRef.current?.getBoundingClientRect().width ?? SEARCHABLE_SELECT_WIDTH;
    setMenuWidth(Math.min(SEARCHABLE_MENU_WIDTH, Math.max(anchorWidth, Math.min(viewportCap, SEARCHABLE_MENU_WIDTH))));
  }, []);

  const getFocusableOptions = useCallback(() => {
    if (!menuListRef.current) return [];
    return Array.from(
      menuListRef.current.querySelectorAll<HTMLElement>(
        '[role="option"]:not([aria-disabled="true"])'
      )
    ).filter((el) => el.getAttribute('data-value') !== '__no_results__');
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    setSearch('');
  }, []);

  const handleOpen = useCallback(() => {
    setOpen(true);
    requestAnimationFrame(() => {
      syncMenuWidth();
      focusSearchInput();
    });
  }, [syncMenuWidth, focusSearchInput]);

  useEffect(() => {
    if (open) {
      syncMenuWidth();
      focusSearchInput();
    }
  }, [open, syncMenuWidth, focusSearchInput]);

  const handleSearchKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    const options = getFocusableOptions();

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      e.stopPropagation();
      options[0]?.focus();
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      e.stopPropagation();
      options[options.length - 1]?.focus();
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      onEnterSelect?.();
      return;
    }

    if (e.key === 'Escape') {
      e.stopPropagation();
      handleClose();
      return;
    }

    e.stopPropagation();
  }, [getFocusableOptions, onEnterSelect, handleClose]);

  const handleMenuListKeyDown = useCallback((e: React.KeyboardEvent<HTMLUListElement>) => {
    if (e.key !== 'ArrowUp') return;

    const options = getFocusableOptions();
    const currentIndex = options.indexOf(e.target as HTMLElement);
    if (currentIndex <= 0) {
      e.preventDefault();
      e.stopPropagation();
      focusSearchInput();
    }
  }, [getFocusableOptions, focusSearchInput]);

  const menuProps = {
    autoFocus: searchable ? false : undefined,
    disableAutoFocusItem: searchable,
    disableEnforceFocus: searchable,
    TransitionProps: {
      onEntered: () => {
        syncMenuWidth();
        focusSearchInput();
      },
    },
    MenuListProps: {
      ref: menuListRef,
      autoFocusItem: false,
      onKeyDown: handleMenuListKeyDown,
    },
    PaperProps: {
      sx: {
        width: menuWidth,
        minWidth: 0,
        maxWidth: 'calc(100vw - 24px)',
        maxHeight: 320,
        mt: 0.5,
        borderRadius: '8px',
        border: `1px solid ${COLORS.lightGray}40`,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      },
    },
  };

  return {
    search,
    setSearch,
    open,
    handleOpen,
    handleClose,
    searchInputRef,
    selectRef,
    menuWidth,
    menuProps,
    handleSearchKeyDown,
    focusSearchInput,
  };
}

interface SearchableSelectSearchBarProps {
  placeholder: string;
  search: string;
  onSearchChange: (value: string) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  resultLabel: string;
  searchInputRef: React.RefObject<HTMLInputElement | null>;
}

export const SearchableSelectSearchBar: React.FC<SearchableSelectSearchBarProps> = ({
  placeholder,
  search,
  onSearchChange,
  onKeyDown,
  resultLabel,
  searchInputRef,
}) => (
  <SearchHeader>
    <SearchFieldWrapper>
      <SearchIcon sx={{ fontSize: 18, color: COLORS.lightGray, flexShrink: 0 }} />
      <DropdownSearchInput
        ref={searchInputRef}
        placeholder={placeholder}
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        onKeyDown={onKeyDown}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        tabIndex={0}
      />
    </SearchFieldWrapper>
    <ResultHint>{resultLabel}</ResultHint>
  </SearchHeader>
);
